import type { Divisao, Representante, Subitem } from '../api'
import { SEM_MEDICAO, foiMedido, vendidoParaAgulha } from '../cartao'
import { faixaDe, proporcao } from '../faixas'
import { larguraBarra, milhoes, valorCurto } from '../formato'
import { usarNumeroAnimado } from '../ganchos/usarNumeroAnimado'
import { Velocimetro } from './Velocimetro'
import { RankingRepresentantes } from './RankingRepresentantes'

/**
 * A visão de UMA divisão — porte de `montarVisaoMarca()` do protótipo.
 * Números grandes à esquerda, o ranking de representante à direita.
 *
 * **O que está à direita mudou em 13/10**: era o feed de pedidos (`FeedPedidos`,
 * que segue no código e tem os seus testes) e passou a ser o ranking de
 * representantes, com ouro, prata e bronze. A soma do ranking fecha com o
 * número do velocímetro ao lado — as duas medidas saem das mesmas linhas do
 * ERP, com a mesma regra de janela aplicada no mesmo lugar. Em 16/10 a lista
 * deixou de ser cortada nos 15 primeiros e passou a vir inteira, rolável; é o
 * que mantém a soma visível igual à soma real da divisão.
 *
 * **Esta tela É o detalhamento.** A tabela "ver valores" do rodapé foi removida
 * em 09/10: não há botão, o painel abre esta visão sozinho na rotação.
 *
 * Leitura, igual ao resto do painel: tudo em reais, nenhum percentual como
 * texto. A meta da divisão fica EMBAIXO DO NOME, o vendido EMBAIXO DO
 * VELOCÍMETRO, e não existe o texto "de R$ X". A proporção só posiciona a
 * agulha e escolhe a cor da faixa.
 *
 * A agulha é MOVIDA, não redesenhada: o `<Velocimetro>` é `memo` comparado pela
 * proporção, e só os dois atributos da linha mudam. Redesenhar o SVG a cada
 * pedido que entra é o piscar que o requisito proíbe.
 */

/* A escala da agulha e o `—` do valor vêm de `cartao.ts` (`vendidoParaAgulha`,
   `SEM_MEDICAO`): é a MESMA regra do card da grade, e duas cópias dela é como
   card e visão de marca passam a discordar sobre o mesmo número. */

function SubitemGrande({ subitem }: { subitem: Subitem }) {
  const p = proporcao(vendidoParaAgulha(subitem.vendido), subitem.meta)

  return (
    <div className="sub-g">
      <span className="sub-g-nome">
        {subitem.rotulo}
        {subitem.obs ? <span className="sub-obs"> ({subitem.obs})</span> : null}
      </span>
      <span className="sub-g-valor">
        {foiMedido(subitem.vendido) ? valorCurto(subitem.vendido) : SEM_MEDICAO}
      </span>
      <div className="trilha-g">
        {/* Largura e cor são DADO, não layout: vêm da proporção, e a transição
            de `.preenche` é o que faz a barra crescer em vez de saltar. */}
        <div
          className="preenche"
          style={{ width: larguraBarra(p), background: faixaDe(p).cor }}
        />
      </div>
      <span className="sub-g-meta">meta {milhoes(subitem.meta)}</span>
    </div>
  )
}

type Props = { divisao: Divisao; representantes: Representante[] }

export function VisaoMarca({ divisao, representantes }: Props) {
  /* O mesmo número subindo do card, agora no tamanho grande (13/10). */
  const vendidoSubindo = usarNumeroAnimado(divisao.vendido)

  return (
    <section className="visao-marca">
      <div className="painel-marca">
        <div className="cabeca-marca">
          <div className="logo-grande">
            {divisao.logo ? <img src={divisao.logo} alt={divisao.nome} /> : divisao.sigla}
          </div>
          <div className="marca-id">
            <div className="nome-grande">{divisao.nome}</div>
            <div className="meta-grande">{milhoes(divisao.meta)}</div>
          </div>
          <div className="bloco-velocimetro">
            <Velocimetro
              proporcao={proporcao(vendidoParaAgulha(divisao.vendido), divisao.meta)}
              classe="vel-marca-grande"
            />
            {/* `vendido: null` mostra `—`, nunca "R$ 0": zero por falha no telão
                é mentira em cima do número. */}
            <div className="vendido-grande">
              {foiMedido(divisao.vendido)
                ? valorCurto(vendidoSubindo ?? divisao.vendido)
                : SEM_MEDICAO}
            </div>
          </div>
        </div>

        <div className="subitens-grandes">
          {divisao.subitens.length === 0 ? (
            /* Private Label, Malhas/Tecidos e Vila Flor são só valor. Dizê-lo
               por escrito evita a leitura de "faltou carregar". */
            <div className="sem-pedido">Esta divisão não é subdividida.</div>
          ) : (
            divisao.subitens.map(subitem => (
              <SubitemGrande key={subitem.codigo} subitem={subitem} />
            ))
          )}
        </div>
      </div>

      {/* `key` pela divisão: trocar de divisão é OUTRO ranking, não o mesmo
          mudando de posição. O `Dashboard` reusa este mesmo lugar da árvore ao
          ir de uma marca direto para outra (seta ← na sala, `?visao=`), e sem a
          `key` as linhas da marca nova entrariam reusando os nós da anterior —
          a medalha de ouro faria a transição de uma pessoa para outra. */}
      <RankingRepresentantes key={divisao.id} representantes={representantes} />
    </section>
  )
}
